import type {ApiErrorBody, ApiErrorCode} from '../dto/apiError';

/**
 * Le résultat d'un appel à l'API vu par un client, sur le modèle de `Try` en Scala :
 * un `Success` qui porte la valeur, ou un `Failure` qui porte la cause — jamais une
 * exception à deviner.
 *
 * `ApiFailure` garde `statusCode` en plus de `code` : le statut dit la famille (et reste
 * lisible par le code qui testait déjà `err.statusCode === 409`), le code dit la cause.
 * `message` est déjà traduit par le serveur ; `details` porte ce qui accompagne certains
 * codes (`page`, `owner`, `candidates`, `retryAfterSeconds`).
 */
export interface ApiFailure {
  ok: false;
  statusCode: number;
  code: ApiErrorCode;
  message: string;
  details: Record<string, unknown>;
  /**
   * L'en-tête `x-amzn-errortype` de la réponse, quand c'est API Gateway elle-même qui refuse
   * (et non notre code) : `MissingAuthenticationTokenException` (requête partie NON signée),
   * `InvalidSignatureException` (signature expirée ou fausse), `UnrecognizedClientException`
   * (identifiants IAM inconnus ou périmés), `AccessDeniedException` (identifiants valables
   * mais refusés)… La liste n'est pas close : rester sur `string`. Absent quand la réponse
   * vient de notre code, qui n'émet pas cet en-tête, ou quand Amplify ne l'a pas fourni.
   */
  gatewayErrorType?: string;
  /** L'erreur d'origine (Amplify, réseau…), pour le journal. Jamais pour décider. */
  cause?: unknown;
}

export interface ApiSuccess<T> {
  ok: true;
  value: T;
}

export type ApiResult<T> = ApiSuccess<T> | ApiFailure;

export const apiSuccess = <T>(value: T): ApiSuccess<T> => ({ok: true, value});

export function isApiFailure(value: unknown): value is ApiFailure {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as {ok?: unknown}).ok === false &&
    typeof (value as {code?: unknown}).code === 'string' &&
    typeof (value as {statusCode?: unknown}).statusCode === 'number'
  );
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as {code?: unknown}).code === 'string' &&
    typeof (value as {message?: unknown}).message === 'string'
  );
}

const GATEWAY_ERROR_TYPE_HEADER = 'x-amzn-errortype';

interface AmplifyErrorResponse {
  statusCode?: number;
  headers?: unknown;
  body?: string | {text?: () => Promise<string>};
}

interface AmplifyErrorParts {
  statusCode?: number;
  body?: unknown;
  gatewayErrorType?: string;
}

/**
 * L'en-tête `x-amzn-errortype` d'une réponse Amplify. Le gestionnaire `fetch` de
 * `@aws-amplify/core` recopie les en-têtes dans un `Record` aux clés en minuscules ; on
 * tolère quand même une autre casse, et n'importe quelle forme pour `headers` (absent, ou
 * autre chose qu'un objet : une erreur venue d'une autre couche).
 */
function readGatewayErrorType(headers: unknown): string | undefined {
  if (typeof headers !== 'object' || headers === null) {
    return undefined;
  }
  const entry = Object.entries(headers as Record<string, unknown>).find(
    ([name]) => name.toLowerCase() === GATEWAY_ERROR_TYPE_HEADER,
  );
  const value = entry?.[1];
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/**
 * Lit le statut, l'en-tête `x-amzn-errortype` et le corps d'une erreur HTTP Amplify. Sur une
 * `RestApiError`, `@aws-amplify/api-rest` a DÉJÀ consommé le flux : `response.body` est le
 * texte du corps. On accepte aussi un corps encore lisible (`.text()`), au cas où l'erreur
 * vient d'une autre couche.
 */
async function readAmplifyErrorBody(error: unknown): Promise<AmplifyErrorParts> {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return {};
  }
  const response = (error as {response?: AmplifyErrorResponse}).response;
  if (typeof response !== 'object' || response === null) {
    return {};
  }
  const parts: AmplifyErrorParts = {statusCode: response.statusCode};
  const gatewayErrorType = readGatewayErrorType(response.headers);
  if (gatewayErrorType !== undefined) {
    parts.gatewayErrorType = gatewayErrorType;
  }
  let text = '';
  try {
    text = typeof response.body === 'string' ? response.body : ((await response.body?.text?.()) ?? '');
  } catch {
    return parts;
  }
  if (text === '') {
    return parts;
  }
  try {
    return {...parts, body: JSON.parse(text) as unknown};
  } catch {
    return {...parts, body: text};
  }
}

/**
 * Convertit ce qu'un `send()` a rejeté en `ApiFailure`. Un corps `{code, message}` du
 * serveur est repris tel quel ; un corps sans code (route pas encore migrée, passerelle)
 * est classé par son statut ; une panne réseau ou une exception quelconque devient
 * `internal_error` (statut 0) avec la cause conservée.
 *
 * Un 403 SANS code est à part : depuis la migration des refus, tout 403 émis par notre code
 * porte un code. Un 403 nu vient donc d'API Gateway elle-même, qui refuse la signature IAM
 * de la requête (« The security token included in the request is invalid. », « Signature
 * expired », « Missing Authentication Token ») : c'est la session qui est à renouveler, pas
 * un droit qui manque. Il est classé `unauthorized`, et `message` garde le texte brut d'AWS
 * pour le journal (cf. `isGatewayForbidden`). L'en-tête `x-amzn-errortype` de la réponse,
 * qui dit POURQUOI la passerelle refuse, est repris dans `gatewayErrorType` : c'est lui qui
 * distingue une requête partie sans signature d'une signature périmée (cf.
 * `isUnsignedRequest`).
 */
export async function toApiFailure(error: unknown): Promise<ApiFailure> {
  if (isApiFailure(error)) {
    return error;
  }
  const {statusCode, body, gatewayErrorType} = await readAmplifyErrorBody(error);
  const gateway = gatewayErrorType !== undefined ? {gatewayErrorType} : {};
  if (isApiErrorBody(body)) {
    const {code, message, ...details} = body;
    return {ok: false, statusCode: statusCode ?? 0, code, message, details, ...gateway, cause: error};
  }
  const legacyMessage =
    typeof body === 'object' && body !== null && typeof (body as {message?: unknown}).message === 'string'
      ? (body as {message: string}).message
      : typeof body === 'string'
        ? body
        : error instanceof Error
          ? error.message
          : String(error);
  return {
    ok: false,
    statusCode: statusCode ?? 0,
    code: codeForStatus(statusCode),
    message: legacyMessage,
    details: {},
    ...gateway,
    cause: error,
  };
}

function codeForStatus(statusCode: number | undefined): ApiErrorCode {
  switch (statusCode) {
    case 400:
      return 'validation_error';
    case 401:
    // Un 403 sans code ne vient que de la passerelle : session à renouveler (cf. `toApiFailure`).
    case 403:
      return 'unauthorized';
    case 404:
      return 'not_found';
    case 409:
      return 'conflict';
    case 413:
      return 'payload_too_large';
    case 429:
      return 'rate_limited';
    case 502:
      return 'upstream_error';
    case 503:
      return 'service_unavailable';
    default:
      return 'internal_error';
  }
}

/**
 * Vrai pour le refus qu'API Gateway oppose à une requête dont la signature IAM n'est plus
 * valable : un 403 sans code PerfectPost, classé `unauthorized` par `toApiFailure`. Le
 * serveur, lui, n'émet `unauthorized` qu'en 401 et accompagne toujours ses 403 d'un code :
 * la combinaison 403 + `unauthorized` désigne donc la passerelle, et elle seule. C'est le
 * signal sur lequel un client renouvelle ses identifiants et rejoue la requête.
 */
export function isGatewayForbidden(failure: ApiFailure): boolean {
  return failure.statusCode === 403 && failure.code === 'unauthorized';
}

/**
 * Vrai quand la passerelle a reçu une requête SANS signature IAM du tout
 * (`x-amzn-errortype: MissingAuthenticationTokenException`) : Amplify n'avait aucun
 * identifiant — pas d'utilisateur Cognito connu, ou un pool d'identités qui refuse les
 * invités — et a envoyé la requête nue. Il n'y a alors rien à renouveler ni à rejouer : le
 * client n'a pas de session, il n'en a pas perdu une. À tester AVANT `isGatewayForbidden`,
 * qui est vrai aussi dans ce cas.
 */
export function isUnsignedRequest(failure: ApiFailure): boolean {
  return failure.gatewayErrorType === 'MissingAuthenticationTokenException';
}
